import { z } from 'zod'

export const INSTRUMENT_CATEGORIES = [
  'Guitars', 'Bass', 'Drums', 'Keyboards', 'Brass',
  'Woodwinds', 'Strings', 'Percussion', 'Synths',
  'Amplifiers', 'Recording Equipment',
] as const

export type InstrumentCategory = typeof INSTRUMENT_CATEGORIES[number]

export const INSTRUMENT_CONDITIONS = [
  'excellent', 'good', 'fair', 'poor',
] as const

export type InstrumentCondition = typeof INSTRUMENT_CONDITIONS[number]

export const PRICING_GUIDE = {
  Guitars: { daily: { min: 1500, max: 4000 }, weekly: { min: 8000, max: 20000 } },
  Bass: { daily: { min: 1500, max: 3500 }, weekly: { min: 7500, max: 17500 } },
  Drums: { daily: { min: 3000, max: 6000 }, weekly: { min: 15000, max: 30000 } },
  Keyboards: { daily: { min: 2000, max: 5000 }, weekly: { min: 10000, max: 25000 } },
  Brass: { daily: { min: 2000, max: 4000 }, weekly: { min: 10000, max: 20000 } },
  Woodwinds: { daily: { min: 1500, max: 3500 }, weekly: { min: 7500, max: 17500 } },
  Strings: { daily: { min: 2000, max: 4500 }, weekly: { min: 10000, max: 22500 } },
  Percussion: { daily: { min: 2000, max: 4500 }, weekly: { min: 10000, max: 22500 } },
  Synths: { daily: { min: 2500, max: 6000 }, weekly: { min: 12500, max: 30000 } },
  Amplifiers: { daily: { min: 2000, max: 5000 }, weekly: { min: 10000, max: 25000 } },
  'Recording Equipment': { daily: { min: 3000, max: 8000 }, weekly: { min: 15000, max: 40000 } },
} as const

export const createInstrumentSchema = z.object({
  title: z
    .string({ error: 'Title is required' })
    .min(3, 'Title must be at least 3 characters')
    .max(200, 'Title must be at most 200 characters'),
  category: z.enum(INSTRUMENT_CATEGORIES, {
    error: 'Please select a valid category',
  }),
  description: z
    .string({ error: 'Description is required' })
    .min(10, 'Description must be at least 10 characters')
    .max(2000, 'Description must be at most 2000 characters'),
  conditionNotes: z
    .string()
    .max(1000, 'Condition notes must be at most 1000 characters')
    .optional(),
  dailyPrice: z
    .number({ error: 'Daily price is required' })
    .int('Daily price must be a whole number (cents)')
    .min(100, 'Daily price must be at least $1.00')
    .max(100000, 'Daily price must be at most $1,000.00'),
  weeklyPrice: z
    .number({ error: 'Weekly price is required' })
    .int('Weekly price must be a whole number (cents)')
    .min(100, 'Weekly price must be at least $1.00')
    .max(500000, 'Weekly price must be at most $5,000.00'),
  value: z
    .number({ error: 'Instrument value is required' })
    .int('Instrument value must be a whole number (cents)')
    .min(1000, 'Instrument value must be at least $10.00')
    .max(10000000, 'Instrument value must be at most $100,000.00'),
  photos: z.array(z.object({
    r2Key: z.string().min(1, 'R2 key is required'),
    altText: z.string().min(1, 'Alt text is required for each photo'),
    displayOrder: z.number().int().min(0).default(0),
  })).optional().default([]),
})

export type CreateInstrumentInput = z.infer<typeof createInstrumentSchema>

export const photoUploadSchema = z.object({
  fileName: z
    .string({ error: 'File name is required' })
    .min(1, 'File name is required'),
  contentType: z.enum(['image/jpeg', 'image/png', 'image/webp'], {
    error: 'Only JPEG, PNG, and WebP images are allowed',
  }),
})

export type PhotoUploadInput = z.infer<typeof photoUploadSchema>

export const updateInstrumentSchema = z.object({
  title: z
    .string()
    .min(3, 'Title must be at least 3 characters')
    .max(200, 'Title must be at most 200 characters')
    .optional(),
  category: z.enum(INSTRUMENT_CATEGORIES, {
    error: 'Please select a valid category',
  }).optional(),
  description: z
    .string()
    .min(10, 'Description must be at least 10 characters')
    .max(2000, 'Description must be at most 2000 characters')
    .optional(),
  conditionNotes: z
    .string()
    .max(1000, 'Condition notes must be at most 1000 characters')
    .optional()
    .nullable(),
  dailyPrice: z
    .number()
    .int('Daily price must be a whole number (cents)')
    .min(100, 'Daily price must be at least $1.00')
    .max(100000, 'Daily price must be at most $1,000.00')
    .optional(),
  weeklyPrice: z
    .number()
    .int('Weekly price must be a whole number (cents)')
    .min(100, 'Weekly price must be at least $1.00')
    .max(500000, 'Weekly price must be at most $5,000.00')
    .optional(),
  value: z
    .number()
    .int('Instrument value must be a whole number (cents)')
    .min(1000, 'Instrument value must be at least $10.00')
    .max(10000000, 'Instrument value must be at most $100,000.00')
    .optional(),
  photos: z.array(z.object({
    r2Key: z.string().min(1, 'R2 key is required'),
    altText: z.string().min(1, 'Alt text is required for each photo'),
    displayOrder: z.number().int().min(0).default(0),
  })).optional(),
})

export type UpdateInstrumentInput = z.infer<typeof updateInstrumentSchema>

export const VALID_INSTRUMENT_STATUSES = ['active', 'paused'] as const

export const instrumentStatusTransitionSchema = z.object({
  status: z.enum(VALID_INSTRUMENT_STATUSES, {
    error: 'Status must be "active" or "paused"',
  }),
})

export type InstrumentStatusTransitionInput = z.infer<typeof instrumentStatusTransitionSchema>

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

export const browseInstrumentsSchema = z.object({
  q: z.string().trim().optional(),
  category: z.enum(INSTRUMENT_CATEGORIES, {
    error: 'Please select a valid category',
  }).optional(),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).default(12).transform(v => Math.min(v, 50)),
  priceMin: z.number().int().min(0).max(2147483647).optional(),
  priceMax: z.number().int().min(0).max(2147483647).optional(),
  area: z.string().trim().optional(),
  availableFrom: z.string().regex(ISO_DATE_REGEX, 'Must be YYYY-MM-DD format').optional(),
  availableTo: z.string().regex(ISO_DATE_REGEX, 'Must be YYYY-MM-DD format').optional(),
}).refine(
  (data) => {
    if (data.priceMin !== undefined && data.priceMax !== undefined) {
      return data.priceMax >= data.priceMin
    }
    return true
  },
  { message: 'priceMax must be greater than or equal to priceMin', path: ['priceMax'] },
).refine(
  (data) => {
    const hasFrom = !!data.availableFrom
    const hasTo = !!data.availableTo
    if (hasFrom !== hasTo) return false
    return true
  },
  { message: 'Both availableFrom and availableTo are required when filtering by availability', path: ['availableTo'] },
).refine(
  (data) => {
    if (data.availableFrom && data.availableTo) {
      return data.availableTo >= data.availableFrom
    }
    return true
  },
  { message: 'availableTo must be on or after availableFrom', path: ['availableTo'] },
)

export type BrowseInstrumentsInput = z.infer<typeof browseInstrumentsSchema>
